import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronDown, BookOpen, Mail } from "lucide-react";
import { useHeaderStore } from "../../store/headerStore";
import "../../styles/help-center.css";

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

const FAQ_LIST: FAQItem[] = [
  {
    id: "faq-password",
    question: "How to change my password?",
    answer:
      "To update your password, click your user avatar in the top-right corner and select Settings > Security. Enter your current password followed by your new password meeting enterprise security complexity (minimum 8 characters with numbers and symbols), and click Save Changes.",
  },
  {
    id: "faq-mfa",
    question: "How can I activate MFA?",
    answer:
      "Multi-Factor Authentication (MFA / TOTP) can be enabled directly under Settings > Security. Scan the generated QR code using Google Authenticator, 1Password, or Authy, and input the 6-digit verification code to secure your Threatactix session.",
  },
  {
    id: "faq-notifications",
    question: "How to turn on notifications?",
    answer:
      "Click 'SET ALERTS' in the top navigation bar or navigate to Alert Config in the sidebar. You can configure custom keyword rules (such as corporate domains, executive names, and brand keywords), set email notification recipients, and integrate real-time webhooks.",
  },
  {
    id: "faq-customer-check",
    question: "How do I search for customer or employee credential leaks?",
    answer:
      "Open Customer Check from the left navigation sidebar. Enter any corporate email address, employee username, or organizational domain to instantly scan hundreds of millions of indexed dark web breaches, stealer log dumps, and paste sites.",
  },
  {
    id: "faq-takedowns",
    question: "How do I request a domain takedown or brand impersonation removal?",
    answer:
      "Navigate to the Takedowns section from the sidebar. Click 'Submit Takedown Request' and provide the malicious URL or lookalike domain along with your company details. Our analysts will handle registrar, host, and cloud provider escalations.",
  },
  {
    id: "faq-reports",
    question: "How do I export threat intelligence feeds and executive reports?",
    answer:
      "Go to Intelligence Feed and click 'Export' to download raw threat feed records as CSV or JSON. For comprehensive executive PDF briefings with MITRE ATT&CK mapping and risk scoring, visit 'EXCLUSIVE REPORTS' in the top header.",
  },
];

export default function HelpCenterPage() {
  const { setPageTitleOverride } = useHeaderStore();
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);

  useEffect(() => {
    setPageTitleOverride("HELP CENTER");
    return () => setPageTitleOverride(null);
  }, [setPageTitleOverride]);

  const toggleFaq = (id: string) => {
    setExpandedFaq((prev) => (prev === id ? null : id));
  };

  return (
    <div className="help-center-container">
      {/* Back button */}
      <Link to="/dashboard" className="help-back-btn">
        <ArrowLeft size={16} />
        <span>Back</span>
      </Link>

      {/* Main Title */}
      <div className="help-page-header">
        <h1 className="help-page-title">How can we help you?</h1>
      </div>

      {/* Hub Grid: Wiki and Contact */}
      <div className="help-hub-grid">
        <Link to="/help-center/wiki" className="help-hub-card">
          <div className="help-hub-card-label">
            <span>Wiki</span>
            <span>&gt;</span>
          </div>
        </Link>

        <Link to="/help-center/contact" className="help-hub-card">
          <div className="help-hub-card-label">
            <span>Contact</span>
            <span>&gt;</span>
          </div>
        </Link>
      </div>

      {/* FAQ Section */}
      <div className="help-faq-section">
        <h2 className="help-section-title">Frequently asked questions</h2>

        <div className="help-faq-list">
          {FAQ_LIST.map((faq) => {
            const isExpanded = expandedFaq === faq.id;
            return (
              <div
                key={faq.id}
                className={`help-faq-item ${isExpanded ? "is-expanded" : ""}`}
              >
                <button
                  type="button"
                  className="help-faq-trigger"
                  onClick={() => toggleFaq(faq.id)}
                  aria-expanded={isExpanded}
                >
                  <span>{faq.question}</span>
                  <ChevronDown
                    size={18}
                    className={`help-faq-chevron ${
                      isExpanded ? "is-rotated" : ""
                    }`}
                  />
                </button>

                {isExpanded && (
                  <div className="help-faq-answer">
                    <p style={{ margin: 0 }}>{faq.answer}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
